import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../shared/context/ThemeContext';
import {
  getDashboardErrorsDrilldown,
  getDashboardLatencyDrilldown,
  getDashboardErrorsOverview,
  getDashboardLatencyOverview,
} from '../../shared/api/api';
import useResizableColumns from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './DashboardDrilldownPage.css';

const METHOD_COLOR = { GET: '#3d8fff', POST: '#bf14cc', PUT: '#ffaa00', DELETE: '#ff4060', PATCH: '#00cc76' };

const ERRORS_TABLE_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp', width: 150 },
  { key: 'method', label: 'Method', width: 90 },
  { key: 'endpoint', label: 'Endpoint', width: 320 },
  { key: 'status', label: 'Status', width: 90 },
  { key: 'duration', label: 'Duration', width: 100 },
  { key: 'lat', label: 'Lat', width: 90 },
];

const LATENCY_TABLE_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp', width: 150 },
  { key: 'method', label: 'Method', width: 90 },
  { key: 'endpoint', label: 'Endpoint', width: 300 },
  { key: 'status', label: 'Status', width: 90 },
  { key: 'duration', label: 'Duration', width: 100 },
  { key: 'latBucket', label: 'Lat Bucket', width: 100 },
  { key: 'error', label: 'Error', width: 90 },
];

function statusClass(code) {
  if (!code) return 'med';
  if (code >= 500) return 'crit';
  if (code >= 400) return 'warn';
  return 'ok';
}

function LatBadge({ lat }) {
  const cls = lat === 'R' ? 'red' : lat === 'A' ? 'amb' : 'grn';
  const label = lat === 'R' ? 'Red' : lat === 'A' ? 'Amber' : 'Green';
  return <span className={`dd-badge dd-badge--${cls}`}>{label}</span>;
}

function StatusBadge({ code }) {
  const cls = statusClass(code);
  return <span className={`dd-badge dd-badge--${cls}`}>{code || '—'}</span>;
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? ts : d.toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'medium' });
}

function formatMs(ms) {
  if (ms == null) return '—';
  if (ms >= 10000) return <span style={{ color: 'var(--red)', fontWeight: 700 }}>{(ms / 1000).toFixed(1)}s</span>;
  if (ms >= 5000)  return <span style={{ color: 'var(--amb)', fontWeight: 700 }}>{(ms / 1000).toFixed(1)}s</span>;
  return <span style={{ color: 'var(--grn)' }}>{ms}ms</span>;
}

function ErrorsTable({ rows, loading, onLoadMore, hasMore, tenantId, fromDate }) {
  const [search, setSearch] = useState('');
  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(ERRORS_TABLE_COLUMNS);
  const filtered = search
    ? rows.filter(r => (r.endpoint || '').toLowerCase().includes(search.toLowerCase()))
    : rows;

  return (
    <div className="dd-table-wrap">
      <div className="dd-toolbar">
        <div className="dd-toolbar-count">
          <strong>{filtered.length}</strong> error{filtered.length !== 1 ? 's' : ''} · {tenantId} · {fromDate}
        </div>
        <input className="dd-search" placeholder="Search endpoint…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading && rows.length === 0 ? (
        <div className="dd-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="dd-empty">No errors found.</div>
      ) : (
        <div className="dd-scroll">
          <table className="dd-table resizable-table" style={{ width: tableWidth }}>
            <colgroup>
              {ERRORS_TABLE_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {ERRORS_TABLE_COLUMNS.map((c) => (
                  <th key={c.key} className="resizable-th">
                    {c.label}
                    <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i}>
                  <td className="dd-ts">{formatTs(r.timestamp)}</td>
                  <td><span className="dd-method" style={{ color: METHOD_COLOR[r.httpMethod] || 'var(--muted)' }}>{r.httpMethod || '—'}</span></td>
                  <td className="dd-ep" title={r.endpoint}>{r.endpoint || '—'}</td>
                  <td><StatusBadge code={r.statusCode} /></td>
                  <td>{formatMs(r.durationMs)}</td>
                  <td><LatBadge lat={r.latBucket} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <div className="dd-load-more">
          <button className="dd-load-more-btn" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function LatencyTable({ rows, loading, onLoadMore, hasMore, tenantId, fromDate, bucket, setBucket }) {
  const [search, setSearch] = useState('');
  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(LATENCY_TABLE_COLUMNS);
  const filtered = search
    ? rows.filter(r => (r.endpoint || '').toLowerCase().includes(search.toLowerCase()))
    : rows;

  return (
    <div className="dd-table-wrap">
      <div className="dd-toolbar">
        <div className="dd-toolbar-count">
          <strong>{filtered.length}</strong> slow call{filtered.length !== 1 ? 's' : ''} · {tenantId} · {fromDate}
        </div>
        <div className="dd-bucket-filters">
          {[['', 'All'], ['R', 'Red'], ['A', 'Amber']].map(([val, label]) => (
            <button key={val}
              className={`dd-chip${bucket === val ? ' dd-chip--active' : ''}${val === 'R' ? ' dd-chip--red' : val === 'A' ? ' dd-chip--amb' : ''}`}
              onClick={() => setBucket(val)}>
              {label}
            </button>
          ))}
        </div>
        <input className="dd-search" placeholder="Search endpoint…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading && rows.length === 0 ? (
        <div className="dd-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="dd-empty">No slow transactions found.</div>
      ) : (
        <div className="dd-scroll">
          <table className="dd-table resizable-table" style={{ width: tableWidth }}>
            <colgroup>
              {LATENCY_TABLE_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {LATENCY_TABLE_COLUMNS.map((c) => (
                  <th key={c.key} className="resizable-th">
                    {c.label}
                    <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i}>
                  <td className="dd-ts">{formatTs(r.timestamp)}</td>
                  <td><span className="dd-method" style={{ color: METHOD_COLOR[r.httpMethod] || 'var(--muted)' }}>{r.httpMethod || '—'}</span></td>
                  <td className="dd-ep" title={r.endpoint}>{r.endpoint || '—'}</td>
                  <td><StatusBadge code={r.statusCode} /></td>
                  <td>{formatMs(r.durationMs)}</td>
                  <td><LatBadge lat={r.latBucket} /></td>
                  <td>{r.error === 'Y' ? <span className="dd-badge dd-badge--crit">Yes</span> : <span className="dd-error-no">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <div className="dd-load-more">
          <button className="dd-load-more-btn" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Single-component drilldown (existing behaviour) ───────────────────────── */
function SingleComponentView({ tenantId, componentName, displayFrom, displayTo }) {
  const [activeTab,  setActiveTab]  = useState('errors');
  const [errRows,    setErrRows]    = useState([]);
  const [latRows,    setLatRows]    = useState([]);
  const [errToken,   setErrToken]   = useState(null);
  const [latToken,   setLatToken]   = useState(null);
  const [errHasMore, setErrHasMore] = useState(false);
  const [latHasMore, setLatHasMore] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [latLoading, setLatLoading] = useState(false);
  const [latBucket,  setLatBucket]  = useState('');
  const [error,      setError]      = useState('');

  const loadErrors = useCallback(async (pageToken = null) => {
    setLoading(true); setError('');
    try {
      const res  = await getDashboardErrorsDrilldown(tenantId, componentName, displayFrom, displayTo, 50, pageToken);
      const data = res.data;
      const items = data?.content || (Array.isArray(data) ? data : []);
      setErrRows(prev => pageToken ? [...prev, ...items] : items);
      setErrToken(data?.nextPageToken || null);
      setErrHasMore(!data?.last && !!data?.nextPageToken);
    } catch { setError('Failed to load errors.'); }
    finally  { setLoading(false); }
  }, [tenantId, componentName, displayFrom, displayTo]);

  const loadLatency = useCallback(async (pageToken = null, bucket = '') => {
    setLatLoading(true);
    try {
      const res  = await getDashboardLatencyDrilldown(tenantId, componentName, displayFrom, displayTo, bucket || null, 50, pageToken);
      const data = res.data;
      const items = Array.isArray(data) ? data : data?.content || [];
      setLatRows(prev => pageToken ? [...prev, ...items] : items);
      setLatToken(data?.nextPageToken || null);
      setLatHasMore(!data?.last && !!data?.nextPageToken);
    } catch {}
    finally { setLatLoading(false); }
  }, [tenantId, componentName, displayFrom, displayTo]);

  useEffect(() => { loadErrors(); },              [loadErrors]);
  useEffect(() => { loadLatency(null, latBucket); }, [loadLatency, latBucket]);

  return (
    <>
      {/* Summary pills */}
      <div className="dd-summary-pills">
        <div className="dd-sum-pill dd-sum-pill--red">
          <div className="dd-sum-val">{errRows.length}</div>
          <div className="dd-sum-lbl">Errors</div>
        </div>
        <div className="dd-sum-pill dd-sum-pill--amb">
          <div className="dd-sum-val">{latRows.filter(r => r.latBucket === 'A').length}</div>
          <div className="dd-sum-lbl">Amber</div>
        </div>
        <div className="dd-sum-pill dd-sum-pill--red2">
          <div className="dd-sum-val">{latRows.filter(r => r.latBucket === 'R').length}</div>
          <div className="dd-sum-lbl">Red</div>
        </div>
        <div className="dd-sum-pill dd-sum-pill--err">
          <div className="dd-sum-val">{latRows.filter(r => r.error === 'Y').length}</div>
          <div className="dd-sum-lbl">Slow + Error</div>
        </div>
      </div>

      {error && <div className="dd-error-bar">{error}</div>}

      <div className="dd-tabs">
        <button className={`dd-tab${activeTab === 'errors' ? ' dd-tab--active' : ''}`} onClick={() => setActiveTab('errors')}>
          HTTP Errors
          <span className={`dd-tab-count${activeTab === 'errors' ? ' dd-tab-count--active' : ''}`}>{errRows.length}</span>
        </button>
        <button className={`dd-tab${activeTab === 'latency' ? ' dd-tab--active' : ''}`} onClick={() => setActiveTab('latency')}>
          Slow Transactions
          <span className={`dd-tab-count dd-tab-count--amb${activeTab === 'latency' ? ' dd-tab-count--active-amb' : ''}`}>{latRows.length}</span>
        </button>
      </div>

      <div className="dd-body">
        {activeTab === 'errors' && (
          <ErrorsTable rows={errRows} loading={loading} onLoadMore={() => loadErrors(errToken)}
            hasMore={errHasMore} tenantId={tenantId} fromDate={displayFrom} toDate={displayTo} />
        )}
        {activeTab === 'latency' && (
          <LatencyTable rows={latRows} loading={latLoading} onLoadMore={() => loadLatency(latToken, latBucket)}
            hasMore={latHasMore} tenantId={tenantId} fromDate={displayFrom}
            bucket={latBucket} setBucket={(b) => { setLatBucket(b); setLatRows([]); }} />
        )}
      </div>
    </>
  );
}

/* ── Per-component section used in the all-components view ─────────────────── */
function ComponentSection({ tenantId, componentName, displayFrom, displayTo, overviewCounts, overviewLoading }) {
  const [expanded,   setExpanded]   = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  const [activeTab,  setActiveTab]  = useState('errors');
  const [errRows,    setErrRows]    = useState([]);
  const [latRows,    setLatRows]    = useState([]);
  const [errToken,   setErrToken]   = useState(null);
  const [latToken,   setLatToken]   = useState(null);
  const [errHasMore, setErrHasMore] = useState(false);
  const [latHasMore, setLatHasMore] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [latLoading, setLatLoading] = useState(false);
  const [latBucket,  setLatBucket]  = useState('');

  const loadErrors = useCallback(async (pageToken = null) => {
    setLoading(true);
    try {
      const res  = await getDashboardErrorsDrilldown(tenantId, componentName, displayFrom, displayTo, 50, pageToken);
      const data = res.data;
      const items = data?.content || (Array.isArray(data) ? data : []);
      setErrRows(prev => pageToken ? [...prev, ...items] : items);
      setErrToken(data?.nextPageToken || null);
      setErrHasMore(!data?.last && !!data?.nextPageToken);
    } catch {}
    finally { setLoading(false); }
  }, [tenantId, componentName, displayFrom, displayTo]);

  const loadLatency = useCallback(async (pageToken = null, bucket = '') => {
    setLatLoading(true);
    try {
      const res  = await getDashboardLatencyDrilldown(tenantId, componentName, displayFrom, displayTo, bucket || null, 50, pageToken);
      const data = res.data;
      const items = Array.isArray(data) ? data : data?.content || [];
      setLatRows(prev => pageToken ? [...prev, ...items] : items);
      setLatToken(data?.nextPageToken || null);
      setLatHasMore(!data?.last && !!data?.nextPageToken);
    } catch {}
    finally { setLatLoading(false); }
  }, [tenantId, componentName, displayFrom, displayTo]);

  const handleExpand = () => {
    setExpanded(e => !e);
    if (!loaded) {
      setLoaded(true);
      loadErrors();
      loadLatency(null, '');
    }
  };

  // Derive pills: use live data when expanded+loaded, overview counts otherwise
  const showOverview = !loaded && overviewCounts !== null && !overviewLoading;
  const dispErrCount = loaded ? errRows.length        : (overviewCounts?.errorCount || 0);
  const dispRedCount = loaded ? latRows.filter(r => r.latBucket === 'R').length : (overviewCounts?.redCount  || 0);
  const dispAmbCount = loaded ? latRows.filter(r => r.latBucket === 'A').length : (overviewCounts?.amberCount || 0);
  const allClear     = dispErrCount === 0 && dispRedCount === 0 && dispAmbCount === 0;

  const severityClass = dispErrCount > 0 || dispRedCount > 0 ? 'crit' : dispAmbCount > 0 ? 'warn' : 'ok';

  return (
    <div className={`dd-comp-section dd-comp-section--${severityClass}${expanded ? ' dd-comp-section--open' : ''}`}>
      <button className="dd-comp-header" onClick={handleExpand}>
        <div className="dd-comp-header-left">
          <span className={`dd-comp-chevron${expanded ? ' dd-comp-chevron--open' : ''}`}>›</span>
          {/* title so the full name survives truncation */}
          <span className="dd-comp-name" title={componentName}>{componentName}</span>
          {(showOverview || loaded) && !overviewLoading && (
            <div className="dd-comp-header-pills">
              {dispErrCount > 0 && (
                <span className="dd-comp-pill dd-comp-pill--err">{dispErrCount} errors</span>
              )}
              {dispRedCount > 0 && (
                <span className="dd-comp-pill dd-comp-pill--red">{dispRedCount} &gt;10s</span>
              )}
              {dispAmbCount > 0 && (
                <span className="dd-comp-pill dd-comp-pill--amb">{dispAmbCount} 5–10s</span>
              )}
              {allClear && (
                <span className="dd-comp-pill dd-comp-pill--ok">✓ All clear</span>
              )}
            </div>
          )}
          {overviewLoading && !loaded && (
            <span className="dd-comp-loading">Loading…</span>
          )}
        </div>
        <span className="dd-comp-toggle-lbl">{expanded ? 'Collapse' : 'Expand'}</span>
      </button>

      {expanded && (
        <div className="dd-comp-body">
          <div className="dd-comp-tabs">
            <button className={`dd-tab${activeTab === 'errors' ? ' dd-tab--active' : ''}`} onClick={() => setActiveTab('errors')}>
              HTTP Errors
              <span className={`dd-tab-count${activeTab === 'errors' ? ' dd-tab-count--active' : ''}`}>{errRows.length}</span>
            </button>
            <button className={`dd-tab${activeTab === 'latency' ? ' dd-tab--active' : ''}`} onClick={() => setActiveTab('latency')}>
              Slow Transactions
              <span className={`dd-tab-count dd-tab-count--amb${activeTab === 'latency' ? ' dd-tab-count--active-amb' : ''}`}>{latRows.length}</span>
            </button>
          </div>

          <div className="dd-comp-panel">
            {activeTab === 'errors' && (
              <ErrorsTable rows={errRows} loading={loading} onLoadMore={() => loadErrors(errToken)}
                hasMore={errHasMore} tenantId={tenantId} fromDate={displayFrom} toDate={displayTo} />
            )}
            {activeTab === 'latency' && (
              <LatencyTable rows={latRows} loading={latLoading} onLoadMore={() => loadLatency(latToken, latBucket)}
                hasMore={latHasMore} tenantId={tenantId} fromDate={displayFrom}
                bucket={latBucket} setBucket={(b) => { setLatBucket(b); setLatRows([]); }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── All-components view ───────────────────────────────────────────────────── */
function AllComponentsView({ tenantId, components, displayFrom, displayTo }) {
  const [overviewMap, setOverviewMap] = useState({});
  const [overviewLoading, setOverviewLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setOverviewLoading(true);
      try {
        const [errRes, latRes] = await Promise.allSettled([
          getDashboardErrorsOverview(tenantId, displayFrom, displayTo),
          getDashboardLatencyOverview(tenantId, displayFrom, displayTo),
        ]);
        if (cancelled) return;
        const map = {};
        if (errRes.status === 'fulfilled') {
          for (const c of (errRes.value.data?.components || [])) {
            map[c.componentName] = { ...map[c.componentName], errorCount: c.errorCount };
          }
        }
        if (latRes.status === 'fulfilled') {
          for (const c of (latRes.value.data?.components || [])) {
            map[c.componentName] = { ...map[c.componentName], redCount: c.redCount, amberCount: c.amberCount };
          }
        }
        setOverviewMap(map);
      } catch {}
      finally { if (!cancelled) setOverviewLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId, displayFrom, displayTo]);

  const totalErrors = Object.values(overviewMap).reduce((s, c) => s + (c.errorCount || 0), 0);
  const totalRed    = Object.values(overviewMap).reduce((s, c) => s + (c.redCount    || 0), 0);
  const totalAmber  = Object.values(overviewMap).reduce((s, c) => s + (c.amberCount  || 0), 0);

  return (
    <div className="dd-all-comps-body">
      {/* Top summary bar */}
      <div className="dd-all-comps-summary">
        <div className="dd-all-comps-intro">
          <span className="dd-all-comps-count">{components.length} components</span>
          <span className="dd-all-comps-hint">Click a component to expand its errors and slow transactions</span>
        </div>
        {!overviewLoading && (
          <div className="dd-all-comps-totals">
            <div className="dd-sum-pill dd-sum-pill--red">
              <div className="dd-sum-val">{totalErrors}</div>
              <div className="dd-sum-lbl">Total Errors</div>
            </div>
            <div className="dd-sum-pill dd-sum-pill--red2">
              <div className="dd-sum-val">{totalRed}</div>
              <div className="dd-sum-lbl">&gt;10s</div>
            </div>
            <div className="dd-sum-pill dd-sum-pill--amb">
              <div className="dd-sum-val">{totalAmber}</div>
              <div className="dd-sum-lbl">5–10s</div>
            </div>
          </div>
        )}
      </div>

      {/* Component list — sorted worst first using overview data */}
      <div className="dd-comp-list">
        {[...components]
          .sort((a, b) => {
            const aScore = (overviewMap[a]?.errorCount || 0) * 3 + (overviewMap[a]?.redCount || 0) * 2 + (overviewMap[a]?.amberCount || 0);
            const bScore = (overviewMap[b]?.errorCount || 0) * 3 + (overviewMap[b]?.redCount || 0) * 2 + (overviewMap[b]?.amberCount || 0);
            return bScore - aScore;
          })
          .map(name => (
            <ComponentSection
              key={name}
              tenantId={tenantId}
              componentName={name}
              displayFrom={displayFrom}
              displayTo={displayTo}
              overviewCounts={overviewMap[name] || null}
              overviewLoading={overviewLoading}
            />
          ))}
      </div>
    </div>
  );
}

/* ── Page root ─────────────────────────────────────────────────────────────── */
export default function DashboardDrilldownPage() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { theme } = useTheme();

  const { tenantId, componentName, components, fromDate, toDate } = location.state || {};

  const today      = new Date().toISOString().slice(0, 10);
  const displayFrom = fromDate || today;
  const displayTo   = toDate   || today;

  const isAllComponents = !componentName && components && components.length > 0;

  if (!tenantId) {
    return (
      <div className={`dd-shell${theme === 'light' ? ' dd-light' : ''}`}>
        <div className="dd-no-state">
          No drilldown context. <button className="dd-back-link" onClick={() => navigate('/dashboard')}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dd-shell${theme === 'light' ? ' dd-light' : ''}`}>

      {/* HEADER */}
      <div className="dd-header">
        <div className="dd-breadcrumb">
          <button className="dd-back-link" onClick={() => navigate('/dashboard')}>← Overview</button>
          <span className="dd-breadcrumb-sep">/</span>
          <span className="dd-breadcrumb-tenant">{tenantId}</span>
          {componentName && (
            <>
              <span className="dd-breadcrumb-sep">/</span>
              <span className="dd-breadcrumb-comp">{componentName}</span>
            </>
          )}
          {isAllComponents && (
            <>
              <span className="dd-breadcrumb-sep">/</span>
              <span className="dd-breadcrumb-comp">All Components</span>
            </>
          )}
        </div>

        <div className="dd-header-meta">
          <div className="dd-header-title">
            {componentName || 'All Components'} — {tenantId}
          </div>
          <div className="dd-header-range">{displayFrom} → {displayTo}</div>
        </div>
      </div>

      {/* BODY — branches on mode */}
      {isAllComponents ? (
        <AllComponentsView
          tenantId={tenantId}
          components={components}
          displayFrom={displayFrom}
          displayTo={displayTo}
        />
      ) : (
        <SingleComponentView
          tenantId={tenantId}
          componentName={componentName}
          displayFrom={displayFrom}
          displayTo={displayTo}
        />
      )}
    </div>
  );
}
